import { useLocalStorageValue } from "@react-hookz/web";
import { IApiSmpPromptType } from "@smp/api";

import {
  FIGURES,
  LINES,
} from "@/components/features/polotno_editor/sections/elements_section/components/paths/paths.static";
import { EAiCardTag } from "@/screens/app/components/ai_assistant/fixtures/cards.types";

type Key = keyof ICustomLocalStorageValue;

export interface ICustomLocalStorageValue {
  "assistant.tag_filter": EAiCardTag[];
  "assistant.favorite_actions": IApiSmpPromptType[];
  "assistant.show_only_favorite_actions": boolean;
  "ep.post_constructor.version": number;
  "ep.tenchat_topics.collapsed_categories": number[];
  "ep.advisor": {
    userId?: number;
    request?: {
      occupation: string;
      socialPageGroupId: number | undefined;
    };
    response?: Array<
      | {
          uid: string;
          title: string;
          text: string;
          unsplash?: string;
        }
      | undefined
    >;
  };
  "ep.advisor_v2": {
    entries: Array<{
      userId?: number;
      request?: {
        agentId: number;
        socialPageGroupId: number | undefined;
      };
      response?: Array<
        | {
            uid: string;
            title: string;
            text: string;
            unsplash?: string;
          }
        | undefined
      >;
    }>;
  };
  "ep.projects": {
    display_no_permission_page_configs: boolean;
  };
  "ep.watermark": {
    preview_setting: Partial<{
      background_id: string;
      aspect_ratio: string;
      size: number;
    }>;
  };
  "ep.polotno.favorite_shapes": Array<{
    url: string;
    preview: string;
    id: string;
  }>;
  "ep.polotno.favorite_paths": Array<
    | {
        type: "line";
        data?: typeof LINES[number]["data"];
        extra_data?: typeof LINES[number]["data"];
        preview?: string;
        original?: string;
        id: string;
      }
    | {
        type: "figure";
        subType: typeof FIGURES[number]["subType"];
        extra_data?: {
          sub_type?: string;
          url?: string;
        };
        preview?: string;
        original?: string;
        url: string;
        id: string;
      }
    | {
        type: "table";
        preview?: string;
        original?: string;
        extra_data: {
          rows: number;
          cols: number;
        };
        url: string;
        id: string;
      }
  >;
  "ep.polotno.favorite_stickers": Array<{
    id: number;
    json_url?: string;
    background_color?: string;
    preview?: string;
    original: string;
    search_strings?: string[];
    is_clip_mask?: boolean;
    [key: string]: any;
  }>;
  "ep.polotno.favorite_texts": Array<{
    json: string;
    preview: string;
  }>;
  "ep.reports.hidden_widgets": string[];
}

type StoredValue<T extends Key, D> = D extends undefined
  ? ICustomLocalStorageValue[T] | null
  : ICustomLocalStorageValue[T];

export const useCustomLocalStorage = <T extends Key, D = undefined>(
  key: T,
  defaultValue?: D,
) =>
  useLocalStorageValue<StoredValue<T, D>>(
    key,
    defaultValue as StoredValue<T, D>,
  );
